import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { faqPageJson } from "@/lib/faq-schema";
import { faqQuestionNodes } from "@/lib/section-faqs";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { medicalWebPageScript, ORG_REF } from "@/lib/medical-schema";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Which weight-loss medications does Dr. Fitzer prescribe?",
    a: "We prescribe the full range, including semaglutide (Wegovy®, Ozempic®), tirzepatide (Zepbound®, Mounjaro®), and the oral medications such as phentermine, Contrave®, orlistat, and diethylpropion. Which one fits depends on your health history, your insurance, and your goals.",
  },
  {
    q: "How much weight can I expect to lose?",
    a: "In trials, semaglutide averaged around 15% of body weight and tirzepatide somewhat more. Individual results vary widely, and they depend heavily on diet and activity alongside the drug.",
  },
  {
    q: "Do I have to take medication forever?",
    a: "For most patients, yes, or at least for a long time. Obesity behaves like a chronic condition. When treatment stops, much of the weight returns.",
  },
  {
    q: "Are these medications covered by insurance in Virginia?",
    a: "It depends entirely on your plan. Some Virginia plans cover weight management fully, others exclude it. We verify your specific benefits before you commit to anything.",
  },
  {
    q: "What are the most common side effects?",
    a: "Nausea, diarrhea, constipation, and vomiting. Most are mild to moderate and settle as you adjust. Starting low and going slow is what keeps them tolerable.",
  },
  {
    q: "Can I use medication after bariatric surgery?",
    a: "Yes. Medication after surgery is increasingly common, particularly for patients who have regained weight or did not reach their goal. The two treatments work well together.",
  },
];


export const Route = createFileRoute("/medical-weight-loss/medications")({
  head: () => ({
    meta: [
      { title: "GLP-1 & Weight-Loss Medications in Northern Virginia | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Semaglutide, tirzepatide, and oral weight loss medications explained by Dr. Fitzer: who qualifies, what to expect, safety warnings, and insurance in Virginia.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "GLP-1 & Weight-Loss Medications in Northern Virginia | Virginia Bariatric" },
        { property: "og:description", content: "Semaglutide, tirzepatide, and oral weight loss medications explained by Dr. Fitzer: who qualifies, what to expect, safety warnings, and insurance in Virginia." },
        { property: "og:url", content: "https://virginiabariatric.com/medical-weight-loss/medications" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: "https://virginiabariatric.com/og/medications/og-medications.png" },
        { property: "og:image:alt", content: "Weight Loss Medications: Introduction — Virginia Bariatric Surgery" },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/og/medications/og-medications.png" },
        { name: "twitter:image:alt", content: "Weight Loss Medications: Introduction — Virginia Bariatric Surgery" },
        { name: "twitter:title", content: "GLP-1 & Weight-Loss Medications in Northern Virginia | Virginia Bariatric" },
        { name: "twitter:description", content: "Semaglutide, tirzepatide, and oral weight loss medications explained by Dr. Fitzer: who qualifies, what to expect, safety warnings, and insurance in Virginia." },
      ] : []),
    ],
    links: [...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/medications") : []),
    ],
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/medical-weight-loss/medications"),
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://virginiabariatric.com/medical-weight-loss/medications#faq",
          // Every visible question on the page — the FAQ block plus the
          // question-style section headings — mapped to FAQPage.
          mainEntity: faqQuestionNodes("/medical-weight-loss/medications", FAQS),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalTherapy",
          "@id": "https://virginiabariatric.com/medical-weight-loss/medications#therapy",
          name: "Weight-Loss Medication Therapy (GLP-1 and GIP/GLP-1)",
          alternateName: ["Anti-obesity medication", "GLP-1 therapy"],
          description:
            "FDA-approved anti-obesity medications used alongside diet, activity, and behavioral support for long-term weight management.",
          url: "https://virginiabariatric.com/medical-weight-loss/medications",
          relevantSpecialty: { "@type": "MedicalSpecialty", name: "Nutrition" },
          study: [
            {
              "@type": "MedicalStudy",
              name: "STEP 1: Once-Weekly Semaglutide in Adults with Overweight or Obesity",
            },
            {
              "@type": "MedicalStudy",
              name: "SURMOUNT-1: Tirzepatide Once Weekly for the Treatment of Obesity",
            },
          ],
          adverseOutcome: {
            "@type": "MedicalEntity",
            name: "Gastrointestinal side effects including nausea, diarrhea, constipation, and vomiting",
          },
          howPerformed:
            "Treatment begins at a low dose and is titrated upward over several weeks, with ongoing monitoring of weight, tolerance, and metabolic markers.",
          recognizingAuthority: ORG_REF,
        }),
      },
    ] : [],
  }),
  component: MedicationPage,
});

function MedicationPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif">Weight-Loss Medications (GLP-1 &amp; More) in Northern Virginia</h1>
        <p className="mt-1 text-foreground/70 italic">Prescription therapies for long-term weight management</p>
      </header>

      <p className="mt-3 text-foreground/80">
        Doctors started prescribing oral medications for weight loss in the mid 1900&rsquo;s. Some of them are quite effective. Unfortunately, patients cannot safely be maintained on them in the long term because of unhealthy side effects.
      </p>
      <TelehealthCta />

      <p className="mt-3 text-foreground/80">
        In the past five years, the new injectable drugs have completely upended medical weight loss practice. They work better than anything we had before, and they have favorable side-effect profiles. They are not risk-free, however, so they have to be used with care.
      </p>
      <p className="mt-3 text-foreground/80">
        This page walks through the main options, who qualifies, what to expect, and what you need to know about safety before you start.
      </p>
      <p className="mt-3 text-foreground/80">
        Dr. Fitzer prescribes and manages these medications at our office at 171 Elden Street, Suite 3B, in Herndon, with telehealth follow-up available.
      </p>


      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">~15%</p>
          <p className="mt-1 text-sm text-foreground/80">average weight loss with semaglutide at 68 weeks</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">up to ~21%</p>
          <p className="mt-1 text-sm text-foreground/80">average weight loss with tirzepatide at 72 weeks</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">Chronic use</p>
          <p className="mt-1 text-sm text-foreground/80">benefits are maintained while treatment continues</p>
        </div>
      </div>

      <nav aria-label="Medication categories" className="mt-6 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <h2 className="text-center text-[1.125rem] font-medium text-foreground/90">Jump to a detailed medication guide</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Link
            to="/medical-weight-loss/semaglutide"
            preload="intent"
            className="flex items-center justify-center rounded-xl bg-brand-logo px-4 py-3 text-sm font-medium text-white transition hover:opacity-90"
          >
            Semaglutide
            <span aria-hidden="true" className="ml-1.5">→</span>
          </Link>
          <Link
            to="/medical-weight-loss/tirzepatide"
            preload="intent"
            className="flex items-center justify-center rounded-xl bg-brand-logo px-4 py-3 text-sm font-medium text-white transition hover:opacity-90"
          >
            Tirzepatide
            <span aria-hidden="true" className="ml-1.5">→</span>
          </Link>
          <Link
            to="/medical-weight-loss/oral-medications"
            preload="intent"
            className="flex items-center justify-center rounded-xl bg-brand-logo px-4 py-3 text-sm font-medium text-white transition hover:opacity-90"
          >
            Oral Medications
            <span aria-hidden="true" className="ml-1.5">→</span>
          </Link>
        </div>
      </nav>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif text-center">Explore each medication</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {[
          {
            to: "/medical-weight-loss/semaglutide" as const,
            title: "Semaglutide",
            sub: "Wegovy® & Ozempic®",
            body: "Once-weekly injection or the new daily pill, with ~15% average weight loss and the strongest heart-protection evidence.",
          },
          {
            to: "/medical-weight-loss/tirzepatide" as const,
            title: "Tirzepatide",
            sub: "Zepbound® & Mounjaro®",
            body: "Dual GIP/GLP-1 injection with the largest average weight loss of any medication — up to ~21%.",
          },
          {
            to: "/medical-weight-loss/oral-medications" as const,
            title: "Oral Medications",
            sub: "Phentermine, Contrave®, orlistat, diethylpropion",
            body: "Oral and lower-cost options when a GLP-1 isn't right, covered, or tolerated, or when it needs a little help.",
          },
        ].map((c) => (
          <Link
            key={c.to}
            to={c.to}
            preload="intent"
            className="group block rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
          >
            <h3 className="text-lg font-serif text-brand-mid">{c.title}</h3>
            <p className="text-sm text-muted-foreground">{c.sub}</p>
            <p className="mt-2 text-sm text-foreground/80 leading-[1.6]">{c.body}</p>
            <span className="mt-2.5 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              Learn more<span aria-hidden="true"> →</span>
            </span>
          </Link>
        ))}
      </div>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">The main weight-loss medication options</h2>

      <p className="mt-3 text-foreground/80">
        Several medications are FDA-approved for long-term weight management. They differ in how they work, how you take them, and how much weight they typically take off.
      </p>

      <h3 className="mt-5 text-[1.15rem] md:text-[1.3rem] font-serif text-brand-mid">Semaglutide (Wegovy)</h3>
      <p className="mt-3 text-foreground/80">
        <Link to="/medical-weight-loss/semaglutide" preload="intent" className="text-brand-deep underline hover:no-underline">Semaglutide</Link> is a once-weekly injection.<sup>1</sup> It copies a natural gut hormone called glucagon like peptide-1, or GLP-1.
      </p>
      <p className="mt-3 text-foreground/80">
        That hormone does three useful things. It tells your brain you are full. It slows how fast your stomach empties. And it helps steady your blood sugar. Together those effects make it much easier to eat less without white-knuckling it.
      </p>

      <h3 className="mt-5 text-[1.15rem] md:text-[1.3rem] font-serif text-brand-mid">Tirzepatide (Zepbound, Mounjaro)</h3>
      <p className="mt-3 text-foreground/80">
        <Link to="/medical-weight-loss/tirzepatide" preload="intent" className="text-brand-deep underline hover:no-underline">Tirzepatide</Link> is also a once-weekly injection, sold as Zepbound for weight management and Mounjaro for diabetes.<sup>2</sup>
      </p>
      <p className="mt-3 text-foreground/80">
        It works on two pathways rather than one. Tirzepatide is a GIP and GLP-1 receptor agonist, meaning it activates both hormone systems at once. In trials, that dual action produced more weight loss than GLP-1 alone. See our comparison of{" "}
        <Link to="/dr-fitzer-dissects-the-news/$slug" params={{ slug: "semaglutide-versus-weight-loss-surgery-outcomes" }} className="text-brand-deep underline hover:no-underline">Wegovy vs. surgery outcomes</Link>{" "}
        for how these drugs stack up against sleeve gastrectomy and gastric bypass over the long term.
      </p>
      <p className="mt-3 text-foreground/80">
        Zepbound also carries an FDA indication no other weight-loss drug has: treating moderate to severe obstructive sleep apnea in adults with obesity. If you have both obesity and sleep apnea, that matters.
      </p>

      <h3 className="mt-5 text-[1.15rem] md:text-[1.3rem] font-serif text-brand-mid">Older and alternative medications</h3>
      <p className="mt-3 text-foreground/80">
        <Link to="/medical-weight-loss/oral-medications" preload="intent" className="text-brand-deep underline hover:no-underline">Phentermine, Contrave, orlistat, and diethylpropion</Link> are still in use. They cost far less than the injections. They also produce less weight loss on average. For some patients, particularly those paying out of pocket, they are the right place to start.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Who is a candidate for weight-loss medication?</h2>
      <p className="mt-3 text-foreground/80">
        These drugs are approved for adults with a body mass index (BMI) of 30 or higher. A BMI of 27 or higher also qualifies if you have a weight-related condition such as type 2 diabetes, high blood pressure, or sleep apnea. Weight loss meds are often used off-label for patients with a BMI below 27. BMI is only the starting point. Each drug carries its own cautions, and some depend on your personal and family medical history. We go through that history with you before prescribing anything. See our{" "}
        <Link to="/resources/qualifications" className="text-brand-deep underline hover:no-underline">bariatric surgery requirements</Link>{" "}
        for a fuller breakdown of BMI thresholds and comorbidities.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">What to expect on weight-loss medication</h2>
      <p className="mt-3 text-foreground/80">
        You start at a low dose and step up gradually over several weeks. That slow ramp is deliberate, and it keeps side effects manageable.
      </p>
      <p className="mt-3 text-foreground/80">
        Most side effects are digestive. Nausea, diarrhea, constipation, and vomiting are the common ones, and they are usually mild to moderate. They tend to ease as your body adjusts.
      </p>
      <p className="mt-3 text-foreground/80">
        One expectation worth setting early: these drugs treat obesity as the ongoing condition it is. Research consistently shows that much of the lost weight comes back when treatment stops. Think of this as long-term care, not a course of antibiotics. If you have already had an operation, our guide to{" "}
        <Link to="/resources/managing-weight-regain-after-bariatric-surgery" preload="intent" className="text-brand-deep underline hover:no-underline">managing weight regain after bariatric surgery</Link>{" "}
        explains how these medications are used after a sleeve or bypass.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Safety: what to know before you start</h2>
      <p className="mt-3 text-foreground/80">
        These are effective medications with a well-documented safety profile. They also carry warnings you should understand before your first injection, not after.
      </p>

      <h3 className="mt-5 text-[1.15rem] md:text-[1.3rem] font-serif text-brand-mid">The thyroid tumor warning</h3>
      <p className="mt-3 text-foreground/80">
        Semaglutide and tirzepatide both carry a boxed warning, which is the FDA's strongest.
      </p>
      <p className="mt-3 text-foreground/80">
        In studies, these drugs caused thyroid C-cell tumors in rats. Whether they do the same in people is not known. Researchers have not been able to determine whether the rat findings apply to humans at all, and the warning reflects that uncertainty rather than proven harm.
      </p>
      <p className="mt-3 text-foreground/80">
        Tell us right away if you notice a lump or swelling in your neck, hoarseness that will not clear, trouble swallowing, or shortness of breath. These can signal a thyroid problem and we will want to look.
      </p>

      <h3 className="mt-5 text-[1.15rem] md:text-[1.3rem] font-serif text-brand-mid">Who should not take these medications</h3>
      <p className="mt-3 text-foreground/80">
        Do not take semaglutide or tirzepatide if you or a family member has had medullary thyroid carcinoma, a type of thyroid cancer.
      </p>
      <p className="mt-3 text-foreground/80">
        The same applies if you have Multiple Endocrine Neoplasia syndrome type 2, usually written MEN 2. This inherited condition raises your risk of certain endocrine tumors. It is why we ask about your family history in more detail than you might expect.
      </p>
      <p className="mt-3 text-foreground/80">
        You also should not take these drugs if you have had a serious allergic reaction to them before.
      </p>

      <h3 className="mt-5 text-[1.15rem] md:text-[1.3rem] font-serif text-brand-mid">Allergic reactions and other risks</h3>
      <p className="mt-3 text-foreground/80">
        Serious allergic reactions, including anaphylaxis and swelling of the face or throat, have been reported since these drugs reached the market. If your face or throat swells, or you have trouble breathing, stop the medication and get help immediately.
      </p>
      <p className="mt-3 text-foreground/80">
        We also screen for pancreatitis, gallbladder disease, and kidney trouble from dehydration. If you have diabetes and take insulin or a sulfonylurea, your blood sugar can drop too low, so we usually adjust those doses when you start.
      </p>
      <p className="mt-3 text-foreground/80">
        None of this is a reason to be afraid of treatment. It is a reason to make sure you use a good doctor.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Cost, access, and considerations</h2>
      <p className="mt-3 text-foreground/80">
        The brand-name GLP-1 and dual-agonist drugs are expensive, and coverage for weight management varies enormously from plan to plan. Some cover them fully. Some exclude them outright. See our{" "}
        <Link to="/medical-weight-loss/insurance" preload="intent" className="text-brand-deep underline hover:no-underline">weight-loss medication insurance guide</Link>, plus deep dives on{" "}
        <Link to="/medical-weight-loss/insurance/medicaid-cova" className="text-brand-deep underline hover:no-underline">Virginia Medicaid and COVA</Link> and{" "}
        <Link to="/medical-weight-loss/insurance/cash-pay-denials" className="text-brand-deep underline hover:no-underline">appealing a denial or paying cash</Link>.
      </p>
      <p className="mt-3 text-foreground/80">
        Our team verifies your benefits, explains what you would actually pay, and walks through financing if you need it.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Next steps: schedule a medical weight-loss visit</h2>
      <p className="mt-3 text-foreground/80">
        The right medication depends on your history, your insurance, and what you are trying to achieve. Request a consultation and we will go through the options with you and start whichever one fits. For patients who need larger or more durable weight loss than medication alone delivers, Dr. Fitzer can also discuss{" "}
        <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery (sleeve gastrectomy)</Link>{" "}
        and other complementary or alternative options.
      </p>


      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/appointments" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
        <Link
          to="/medical-weight-loss/insurance"
          className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90 text-center"
        >
          Insurance Coverage Guide
        </Link>
      </div>


      <section aria-labelledby="medication-faqs">
        <h2 id="medication-faqs" className="mt-8 text-[1.35rem] md:text-[1.6875rem] font-serif text-center">
          Weight-loss medication FAQs
        </h2>
        <dl className="mt-4 space-y-5">
          {FAQS.map((faq) => (
            <div key={faq.q}>
              <dt className="font-medium">{faq.q}</dt>
              <dd className="mt-1 text-foreground/80">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </section>



      <h2 className="mt-8 text-[1.35rem] md:text-[1.6875rem] font-serif">Additional Reading</h2>
      <p className="mt-3 text-foreground/80">
        Read Dr. Fitzer's articles about Weight Loss Medications: Introduction for more information.
      </p>
      <ul className="mt-3 list-disc space-y-1 pl-5 text-foreground/80">
        <li>
          <Link to="/dr-fitzer-dissects-the-news/$slug" params={{ slug: "weight-regain-after-wegovy-zepbound-treatment-part-3" }} className="text-brand-deep underline hover:no-underline">
            Weight Regain After Wegovy/Zepbound Treatment?—Part 3
          </Link>
        </li>
        <li>
          <Link to="/dr-fitzer-dissects-the-news/$slug" params={{ slug: "weight-regain-after-wegovy-zepbound-treatment-part-2" }} className="text-brand-deep underline hover:no-underline">
            Weight Regain After Wegovy/Zepbound Treatment?—Part 2
          </Link>
        </li>
        <li>
          <Link to="/dr-fitzer-dissects-the-news/$slug" params={{ slug: "weight-regain-after-wegovy-zepbound-treatment-part-1" }} className="text-brand-deep underline hover:no-underline">
            Weight Regain After Wegovy/Zepbound Treatment?—Part 1
          </Link>
        </li>
        <li>
          <Link to="/dr-fitzer-dissects-the-news/$slug" params={{ slug: "weight-regain-after-stopping-injectable-medication-whats-the-reality" }} className="text-brand-deep underline hover:no-underline">
            Will I Regain when I stop Wegovy/Zepbound?
          </Link>
        </li>
        <li>
          <Link to="/dr-fitzer-dissects-the-news/$slug" params={{ slug: "semaglutide-versus-weight-loss-surgery-outcomes" }} className="text-brand-deep underline hover:no-underline">
            Wegovy vs. Surgery Outcomes
          </Link>
        </li>
      </ul>

      <div className="mt-4 text-sm text-foreground/70">
        <p className="font-medium">Content informed by research indexed in PubMed:</p>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>
            Wilding JPH, et al. Once-Weekly Semaglutide in Adults with Overweight or Obesity (STEP 1). <em>N Engl J Med</em>. 2021;384:989-1002. doi:10.1056/NEJMoa2032183
          </li>
          <li>
            Jastreboff AM, et al. Tirzepatide Once Weekly for the Treatment of Obesity (SURMOUNT-1). <em>N Engl J Med</em>. 2022;387:205-216. doi:10.1056/NEJMoa2206038
          </li>
        </ol>
      </div>
      <LastUpdated className="mt-10" />
      </article>
  );
}
